import {
  collection, doc, getDocs, addDoc, updateDoc,
  serverTimestamp, query, where,
} from 'firebase/firestore';
import { signInAnonymously } from 'firebase/auth';
import { db, auth } from '../config/firebase';
import type { Review360Invite, Review360Response } from '../types';

const COL_INVITE   = '360Invites';
const COL_RESPONSE = '360Responses';

function generateToken(): string {
  const arr = new Uint8Array(24);
  crypto.getRandomValues(arr);
  return Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
}

// ─── Invites ──────────────────────────────────────────────────────────────────

/**
 * Generate N invite tokens for a review form.
 * Returns the public 360 links so HR can copy/paste or email them.
 */
export async function generateInvites(
  reviewFormId: string,
  revieweeId: string,
  revieweeName: string,
  cycleId: string,
  count: number,
  baseUrl: string,
  expiryDays: number = 14,
): Promise<{ token: string; url: string }[]> {
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + expiryDays);

  const results: { token: string; url: string }[] = [];

  for (let i = 0; i < count; i++) {
    const token = generateToken();
    await addDoc(collection(db, COL_INVITE), {
      token,
      reviewFormId,
      revieweeId,
      revieweeName,
      cycleId,
      status: 'pending',
      expiresAt,
      createdAt: serverTimestamp(),
    });
    results.push({ token, url: `${baseUrl}/360/${token}` });
  }

  return results;
}

export async function fetchInvitesForForm(reviewFormId: string): Promise<Review360Invite[]> {
  const snap = await getDocs(
    query(collection(db, COL_INVITE), where('reviewFormId', '==', reviewFormId)),
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as Review360Invite);
}

/** Look up invite by token — used on the public 360 form page */
export async function getInviteByToken(token: string): Promise<Review360Invite | null> {
  const snap = await getDocs(
    query(collection(db, COL_INVITE), where('token', '==', token)),
  );
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() } as Review360Invite;
}

// ─── Responses ────────────────────────────────────────────────────────────────

/**
 * Submit a 360 response.
 * Signs in anonymously first so Firestore rules allow the write.
 */
export async function submitResponse(
  invite: Review360Invite,
  opticsScores: Record<string, number>,
): Promise<void> {
  // Ensure we have auth (anonymous)
  if (!auth.currentUser) {
    await signInAnonymously(auth);
  }
  const responderId = auth.currentUser!.uid;

  // 1. Save response
  await addDoc(collection(db, COL_RESPONSE), {
    inviteId: invite.id,
    reviewFormId: invite.reviewFormId,
    revieweeId: invite.revieweeId,
    cycleId: invite.cycleId,
    responderId,
    opticsScores,
    createdAt: serverTimestamp(),
  });

  // 2. Mark invite completed
  await updateDoc(doc(db, COL_INVITE, invite.id), {
    status: 'completed',
    responderId,
  });

  // 3. Append response ID reference to reviewForm
  const { arrayUnion } = await import('firebase/firestore');
  // We get the new response doc id by querying (or we can store ref)
  const respSnap = await getDocs(
    query(
      collection(db, COL_RESPONSE),
      where('inviteId', '==', invite.id),
      where('responderId', '==', responderId),
    ),
  );
  if (!respSnap.empty) {
    await updateDoc(doc(db, 'reviewForms', invite.reviewFormId), {
      responses360Ids: arrayUnion(respSnap.docs[0].id),
    });
  }
}

export async function fetchResponsesForForm(reviewFormId: string): Promise<Review360Response[]> {
  const snap = await getDocs(
    query(collection(db, COL_RESPONSE), where('reviewFormId', '==', reviewFormId)),
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as Review360Response);
}

/** Compute averaged 360 OPTICS scores from all responses for a form */
export function aggregate360Scores(
  responses: Review360Response[],
): Record<string, number> {
  if (!responses.length) return {};

  const totals: Record<string, number> = {};
  const counts: Record<string, number> = {};

  for (const r of responses) {
    for (const [key, val] of Object.entries(r.opticsScores)) {
      totals[key] = (totals[key] ?? 0) + val;
      counts[key] = (counts[key] ?? 0) + 1;
    }
  }

  return Object.fromEntries(
    Object.keys(totals).map(k => [k, parseFloat((totals[k] / counts[k]).toFixed(3))]),
  );
}
